import { epochDayOf } from '@kc/core/model/ids'
import type { DeviceId } from '@kc/core/model/ids'
import { seededRng } from '@kc/core/ports'
import type { Clock, Ports, Rng, SyncReport } from '@kc/core/ports'

import { ContentFilesPort } from './files-port'
import { IndexedDbReviewLog } from './review-log'
import { LocalSettingsPort } from './settings-port'

const DEVICE_KEY = 'kc.device.v1'

function randomHex(bytes: number): string {
  const data = new Uint8Array(bytes)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(data)
  } else {
    for (let i = 0; i < bytes; i++) data[i] = Math.floor(Math.random() * 256)
  }
  return [...data].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/** 设备标识只在本机生成一次；换浏览器就是另一份日志来源。 */
class BrowserDevice {
  #cached: DeviceId | null = null

  async get(): Promise<DeviceId> {
    if (this.#cached !== null) return this.#cached

    try {
      const stored = localStorage.getItem(DEVICE_KEY)
      if (stored !== null && /^dev-[0-9a-f]{8}$/.test(stored)) {
        this.#cached = stored as DeviceId
        return this.#cached
      }
    } catch {
      // 继续生成。
    }

    const device = `dev-${randomHex(4)}` as DeviceId
    try {
      localStorage.setItem(DEVICE_KEY, device)
    } catch {
      // 内存里仍有值。
    }
    this.#cached = device
    return device
  }
}

const clock: Clock = {
  today: () => epochDayOf(new Date()),
  nowMs: () => Date.now(),
}

function createRng(): Rng {
  const seed =
    typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
      ? crypto.getRandomValues(new Uint32Array(1))[0] ?? Date.now()
      : Date.now()
  return seededRng(seed >>> 0)
}

export interface BrowserPortsBundle {
  readonly ports: Ports
  readonly files: ContentFilesPort
  readonly reviews: IndexedDbReviewLog
  readonly settings: LocalSettingsPort
  readonly device: BrowserDevice
}

export function createBrowserPorts(files: ContentFilesPort): BrowserPortsBundle {
  const reviews = new IndexedDbReviewLog()
  const settings = new LocalSettingsPort()
  const device = new BrowserDevice()

  const ports: Ports = {
    files,
    reviews,
    settings,
    clock,
    rng: createRng(),
    device: () => device.get(),
    // 内容同步由 ContentSource 负责；Workspace.pull() 在当前客户端里是空操作。
    pullRepo: async (): Promise<SyncReport> => ({ status: 'up-to-date' }),
  }

  return { ports, files, reviews, settings, device }
}
